import { Label } from '@components/ui/label';
import { cn } from '@lib/utils';
import { useStore } from '@tanstack/react-form';
import { type ReactNode } from 'react';
import { TooltipComponent } from '../../core/tooltip.component';
import { useFieldContext } from './form';

type FormFieldProps = {
  children: ReactNode;
  className?: string;
};

/**
 * A container component that provides consistent spacing and layout for form fields.
 * Wraps field components with standardized spacing and styling.
 * @param props - Children to render and optional className for styling
 * @returns A styled container for form field components
 */
export function FormField(props: FormFieldProps) {
  const { children, className } = props;
  return <div className={cn('space-y-1', className)}>{children}</div>;
}

type FormLabelProps = {
  children: ReactNode;
  className?: string;
  htmlFor?: string;
  /** Small adornment rendered after the label text (e.g. an AI-provenance chip). */
  labelSuffix?: ReactNode;
  required?: boolean;
  tooltip?: ReactNode;
};

/**
 * A label component optimized for form fields with accessibility support.
 * Provides proper association with form controls via htmlFor attribute.
 * @param props - Label content, styling, and form control association
 * @returns An accessible label component for form fields
 */
export function FormLabel(props: FormLabelProps) {
  const { children, className, htmlFor, labelSuffix, required, tooltip } = props;
  const field = useFieldContext();
  const errors = useStore(field.store, (state) => state.meta.errors);
  const isTouched = useStore(field.store, (state) => state.meta.isTouched);
  const error = errors?.[0];

  const labelContent = (
    <>
      {required && <span className="text-destructive pr-1">*</span>}
      {children}
    </>
  );

  if (tooltip || labelSuffix) {
    return (
      <div className="flex items-center gap-1.5">
        <Label
          className={cn('text-gray-700 leading-5', className, isTouched && error && 'text-destructive')}
          htmlFor={htmlFor}
        >
          {labelContent}
        </Label>
        {labelSuffix}
        {tooltip && (
          <TooltipComponent
            className="text-xs"
            side="right"
            triggerClassName="text-gray-500 bg-white"
          >
            {tooltip}
          </TooltipComponent>
        )}
      </div>
    );
  }

  return (
    <Label
      className={cn('text-gray-700 leading-5', className, isTouched && error && 'text-destructive')}
      htmlFor={htmlFor}
    >
      {labelContent}
    </Label>
  );
}

type FormDescriptionProps = {
  children: ReactNode;
  className?: string;
};

/**
 * A description component that provides additional context or instructions for form fields.
 * Styled as muted text to provide helpful information without overwhelming the interface.
 * @param props - Description content to display
 * @returns A styled description component for form field context
 */
export function FormDescription(props: FormDescriptionProps) {
  const { children, className } = props;
  return <p className={cn('text-xs text-muted-foreground', className)}>{children}</p>;
}

type FormErrorMessageProps = {
  message?: string;
};

/**
 * An error display component that shows field validation errors.
 * Automatically retrieves and displays errors from the field context, only showing
 * errors after the field has been touched by the user.
 * @returns A list of validation errors with proper ARIA roles for accessibility
 */
export function FormErrorMessage(props: FormErrorMessageProps) {
  const { message } = props;
  const field = useFieldContext();
  const errors = useStore(field.store, (state) => state.meta.errors);
  const isTouched = useStore(field.store, (state) => state.meta.isTouched);

  const error = message || errors?.[0];
  if (!error) return null;

  return (
    <div
      className="text-destructive text-xs"
      key={error ? (typeof error === 'string' ? error : error.message) : `error-${field.name}`}
      role="alert"
    >
      {isTouched && error && (typeof error === 'string' ? error : error.message)}
    </div>
  );
}
